/*
 * Farwings assistant, browser side.
 *
 * ask() sends the conversation to /api/chat, where the intent model runs on the
 * server (api/chat.js). If the server cannot be reached, a small keyword
 * matcher picks the intent here instead, using the same answers
 * (src/lib/botAnswers.js), so the assistant always works.
 */
import { answerFor, directAnswer, guardPrices, CONTACT } from './botAnswers';

export { CONTACT };

export const SUGGESTIONS = [
  'What do you build?',
  'Show me your clients',
  'Take me to the reviews',
  'How long does a project take?',
  'Talk to a person',
];

// offline fallback: keyword groups -> intent id (ids match bot-model/intents.py)
const RULES = [
  ['price', ['price', 'cost', 'costs', 'budget', 'quote', 'quotation', 'charge', 'charges', 'rate', 'rates', 'kitna', 'expensive', 'cheap', 'pricing', 'fees', 'fee', 'discount', 'payment', 'pay', 'advance', 'aed', 'inr', 'rupees', 'dollars']],
  ['reviews', ['reviews', 'review', 'testimonials', 'testimonial', 'feedback', 'ratings', 'trust']],
  ['why', ['why', 'different', 'better', 'special', 'experience', 'reliable']],
  ['blog', ['blog', 'articles', 'article', 'posts', 'guides']],
  ['faq', ['faq', 'questions']],
  ['time', ['long', 'timeline', 'weeks', 'week', 'months', 'deadline', 'fast', 'quickly', 'soon', 'duration', 'days']],
  ['contact', ['contact', 'phone', 'call', 'number', 'email', 'mail', 'whatsapp', 'human', 'person', 'talk', 'speak', 'reach', 'meeting']],
  ['work', ['work', 'portfolio', 'projects', 'project', 'examples', 'clients', 'client', 'case', 'previous', 'samples']],
  ['support', ['support', 'maintenance', 'maintain', 'bugs', 'hosting', 'after']],
  ['web', ['website', 'web', 'site', 'landing', 'ecommerce', 'e-commerce', 'store', 'shop', 'react', 'nextjs', 'wordpress', 'catalog', 'catalogue']],
  ['mobile', ['app', 'apps', 'mobile', 'android', 'ios', 'iphone', 'flutter', 'playstore', 'appstore']],
  ['ai', ['ai', 'automation', 'automate', 'chatbot', 'gpt', 'llm', 'openai', 'agent', 'agents', 'workflow']],
  ['saas', ['saas', 'mvp', 'startup', 'subscription', 'platform']],
  ['software', ['software', 'erp', 'crm', 'dashboard', 'backend', 'api', 'inventory', 'billing', 'invoice', 'admin', 'system', 'portal']],
  ['seo', ['seo', 'google', 'ranking', 'rank', 'traffic', 'visibility']],
  ['tech', ['tech', 'stack', 'technology', 'technologies', 'framework', 'language', 'node', 'python', 'database']],
  ['process', ['process', 'steps', 'begin', 'method', 'approach', 'onboarding', 'methodology']],
  ['location', ['where', 'located', 'location', 'based', 'office', 'gujarat', 'india', 'ahmedabad', 'remote']],
  ['who', ['bot', 'robot', 'human', 'real']],
  ['services', ['services', 'service', 'offer', 'offers', 'build', 'provide', 'specialise', 'specialize']],
  ['hello', ['hi', 'hello', 'hey', 'hii', 'salam', 'assalam', 'namaste']],
  ['thanks', ['thanks', 'thank', 'thx', 'bye', 'shukriya']],
];

const tokenize = (t) => t.toLowerCase().replace(/[^a-z0-9\s-]/g, ' ').split(/\s+/).filter(Boolean);

// the suggestion chips answer instantly, no server round trip
const INSTANT = {
  'what do you build?': 'services',
  'show me your clients': 'work',
  'how long does a project take?': 'time',
  'talk to a person': 'contact',
};

const reply = (a, source) => ({ text: guardPrices(a.text), links: a.links, whatsapp: a.whatsapp, go: a.go || null, source });

/** loads the model on the server ahead of the first question */
let warmed = false;
export function warmUp() {
  if (warmed || typeof fetch === 'undefined') return;
  warmed = true;
  fetch('/api/chat').catch(() => {});
}

export function localAnswer(text) {
  const direct = directAnswer(text);
  if (direct) return reply(direct, 'site');
  const words = new Set(tokenize(text));
  let best = 'fallback';
  let bestHits = 0;
  for (const [id, keys] of RULES) {
    const hits = keys.filter((w) => words.has(w)).length;
    if (hits > bestHits) {
      best = id;
      bestHits = hits;
    }
  }
  return reply(answerFor(best), 'site');
}

/** messages: [{ role: 'user' | 'assistant', content }] */
export async function ask(messages) {
  const last = messages[messages.length - 1]?.content || '';
  const instant = INSTANT[last.trim().toLowerCase()];
  if (instant) return reply(answerFor(instant), 'site');
  const direct = directAnswer(last); // navigation, clients and pricing answer instantly
  if (direct) return reply(direct, 'site');

  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 7000); // slow server: the offline matcher answers instead
  try {
    const res = await fetch('/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: messages.slice(-12) }),
      signal: ctrl.signal,
    });
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data.reply === 'string' && data.reply.trim()) {
        return { text: guardPrices(data.reply.trim()), links: data.links, whatsapp: data.whatsapp, go: data.go || null, source: data.source || 'model' };
      }
    }
  } catch {
    /* server unreachable: answer here */
  } finally {
    clearTimeout(t);
  }
  return localAnswer(last);
}
